import { useEffect, useMemo, useState } from 'react';

import type { EvidenceItem } from '../../types';
import {
  type DealPickerResult,
  useDealPickerSearch,
  useUnbindEvent,
} from '../../hooks/useDeals';
import { formatEtShort, useDebounced } from './DealsPage.shared';
import styles from './DealsPage.module.css';

/**
 * Deals-tab modal dialogs (refute evidence / log pass / merge), extracted
 * verbatim from DealsPage.tsx (D4 componentization, zero behavior change).
 */

// 2026-05-26 — Refute-evidence modal. Three options, single button.
//   - "Doesn't belong" / "Just noise"   → unbind (flip to noise)
//   - "Belongs to a different deal"     → rebind (write new row, flip old)
// Backend `unbind_event` semantic is durable across rescans now (PR #190
// follow-up): flips resolution_status='noise' instead of deleting.
type RefuteMode = 'unbind' | 'rebind' | 'noise';

export function RefuteEvidenceModal({
  ev,
  dealKey,
  onClose,
}: {
  ev: EvidenceItem;
  dealKey: string;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<RefuteMode>('unbind');
  const [reason, setReason] = useState('');
  const [picked, setPicked] = useState<DealPickerResult | null>(null);
  const [query, setQuery] = useState('');
  const [success, setSuccess] = useState<null | { message: string }>(null);
  const debouncedQuery = useDebounced(query, 200);
  const search = useDealPickerSearch(debouncedQuery, mode === 'rebind' && picked === null);
  const mutation = useUnbindEvent();

  const reasonValid = reason.trim().length > 0 && reason.length <= 500;
  const targetValid = mode !== 'rebind' || picked !== null;
  const canSubmit = reasonValid && targetValid && !mutation.isPending && !success;

  // Auto-close after a brief success display so the user sees confirmation.
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => onClose(), 1200);
    return () => window.clearTimeout(id);
  }, [success, onClose]);

  const handleSubmit = () => {
    const successMessage =
      mode === 'rebind' && picked
        ? `✓ Moved to ${picked.label}`
        : mode === 'noise'
          ? '✓ Marked as noise'
          : '✓ Unbound from this deal';
    mutation.mutate(
      {
        dealKey,
        eventLogId: ev.event_log_id,
        reason: reason.trim(),
        rebindDealKind:
          mode === 'rebind' && picked ? picked.kind : undefined,
        rebindDealKey:
          mode === 'rebind' && picked ? picked.deal_key : undefined,
      },
      {
        // Queue-only Phase B (owner ruling 2026-08-20) — a refute re-aggregates
        // the affected deal(s) via an enqueued job, not inline, so say so.
        onSuccess: (data) => {
          const queued = (data?.aggregated?.length ?? 0) > 0;
          setSuccess({
            message: queued
              ? `${successMessage} Suggestions will refresh shortly.`
              : successMessage,
          });
        },
      },
    );
  };

  return (
    <div
      className={styles.refuteModalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.refuteModal}>
        <h3 style={{ marginTop: 0, color: '#333' }}>Refute this evidence</h3>

        <div
          style={{
            fontSize: 12,
            color: '#666',
            background: '#F8F8F4',
            padding: 8,
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 600, color: '#333' }}>{ev.subject || '(no subject)'}</div>
          <div>from {ev.sender ?? '—'} → {ev.recipients_to.join(', ') || '—'}</div>
          <div>{ev.occurred_at ? formatEtShort(ev.occurred_at) : '—'}</div>
        </div>

        {success && (
          <div
            style={{
              background: '#E8F5E0',
              color: '#0E7F3A',
              padding: 12,
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 12,
              textAlign: 'center',
            }}
          >
            {success.message}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>What's wrong?</div>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
            <input
              type="radio"
              checked={mode === 'unbind'}
              onChange={() => setMode('unbind')}
            />{' '}
            Doesn't belong to this deal
          </label>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
            <input
              type="radio"
              checked={mode === 'rebind'}
              onChange={() => setMode('rebind')}
            />{' '}
            Belongs to a different deal
          </label>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
            <input
              type="radio"
              checked={mode === 'noise'}
              onChange={() => setMode('noise')}
            />{' '}
            This email is just noise (forward, OOO, FYI)
          </label>
        </div>

        {mode === 'rebind' && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Target deal</div>
            {picked ? (
              <div style={{ marginTop: 6 }}>
                <span className={styles.refutePickerSelectedChip}>
                  {picked.label}
                  {' · '}
                  <span style={{ color: '#666' }}>
                    {picked.kind === 'candidate'
                      ? 'candidate'
                      : `opp ${picked.deal_key}`}
                  </span>
                  {' '}
                  <button
                    type="button"
                    onClick={() => setPicked(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    ✕
                  </button>
                </span>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className={styles.refuteSearchInput}
                  placeholder="Search by codename / company / opp id…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                {search.isLoading && debouncedQuery && (
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Searching…</div>
                )}
                {search.data && (
                  <div className={styles.refutePickerResults}>
                    {search.data.candidates.length === 0 && search.data.affinity_opps.length === 0 && (
                      <div style={{ padding: 10, fontSize: 12, color: '#888' }}>
                        No matches. Try a codename, company name, or paste an opp_id.
                      </div>
                    )}
                    {search.data.candidates.map((r) => (
                      <div
                        key={`cand-${r.deal_key}`}
                        className={styles.refutePickerRow}
                        onClick={() => setPicked(r)}
                      >
                        <span>
                          <strong>{r.label}</strong>
                          {r.sublabel && <span style={{ color: '#888' }}> · {r.sublabel}</span>}
                        </span>
                        <span style={{ color: '#888', fontSize: 11 }}>candidate</span>
                      </div>
                    ))}
                    {search.data.affinity_opps.map((r) => (
                      <div
                        key={`opp-${r.deal_key}`}
                        className={styles.refutePickerRow}
                        onClick={() => setPicked(r)}
                      >
                        <span>
                          <strong>{r.label}</strong>
                          {r.stage && <span style={{ color: '#888' }}> · {r.stage}</span>}
                        </span>
                        <span style={{ color: '#888', fontSize: 11 }}>opp {r.deal_key}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
            Reason (required, ≤500 chars)
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. mentions Project Lift only in passing — the email is about Caban Energy."
            style={{
              width: '100%',
              minHeight: 72,
              fontSize: 12,
              padding: 6,
              boxSizing: 'border-box',
              border: '1px solid #DDD',
              borderRadius: 4,
              fontFamily: 'inherit',
            }}
          />
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
            {reason.length}/500
          </div>
        </div>

        {mutation.error && (
          <div
            style={{
              background: '#FDE6E6',
              color: '#A02020',
              padding: 6,
              borderRadius: 4,
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            {(mutation.error as Error).message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              background: canSubmit ? '#C62828' : '#CCC',
              color: '#FFF',
              border: 'none',
              padding: '6px 14px',
              borderRadius: 4,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {mutation.isPending ? 'Refuting…' : 'Refute'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Slice J §J4 — Log Pass categories.
// Backend (deal_logger.log_pass) validates "Other" as the sentinel that
// leaves Affinity Lost Passed Category blank and parks the note in
// lost_passed_notes only.
const LOG_PASS_REASONS_ORDER: readonly string[] = [
  'Off Strategy',
  'ESG Concern',
  'Insufficient Growth Opportunity',
  'Management Team Concerns',
  'Risk Profile',
  'Too Early Stage',
  'Valuation Expectations',
];
const LOG_PASS_OTHER = 'Other';

interface LogPassModalProps {
  subjectLabel: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (body: { category: string; note?: string }) => void;
}

export function LogPassModal({ subjectLabel, submitting, onClose, onSubmit }: LogPassModalProps) {
  const [category, setCategory] = useState<string>(LOG_PASS_REASONS_ORDER[0]);
  const [note, setNote] = useState('');
  const otherSelected = category === LOG_PASS_OTHER;
  const noteRequired = otherSelected;
  const canSubmit =
    !submitting && !!category && (!noteRequired || note.trim().length > 0);

  return (
    <div
      className={styles.modalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* role="dialog": a11y, and the F.8 record-nav guard blocks the arrow keys while any
          non-record-panel dialog is mounted. This modal takes no focus of its own, so without
          the role a stray `→` behind it could swap the record and land a TERMINAL pass on
          the wrong one. */}
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Log Pass">
        <h3 className={styles.modalTitle}>Log Pass</h3>
        <div className={styles.modalSubtitle}>{subjectLabel}</div>
        <p style={{ fontSize: 13, color: '#1A1A1A', margin: '8px 0' }}>
          Recording a terminal pass on Affinity at stage = <strong>Passed</strong>.
          Pick a Lost Passed Category so the team can see why we passed.
        </p>
        <div className={styles.logPassReasons}>
          {LOG_PASS_REASONS_ORDER.map((opt) => (
            <label key={opt} className={styles.logPassReasonRow}>
              <input
                type="radio"
                name="log-pass-category"
                checked={category === opt}
                onChange={() => setCategory(opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
          <label key={LOG_PASS_OTHER} className={styles.logPassReasonRow}>
            <input
              type="radio"
              name="log-pass-category"
              checked={otherSelected}
              onChange={() => setCategory(LOG_PASS_OTHER)}
            />
            <span>Other (logged in commentary only — explain below)</span>
          </label>
        </div>
        <textarea
          className={styles.noteInput}
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            noteRequired
              ? 'Required: explain why we passed (saved as Lost Passed Commentary)'
              : 'Optional commentary (saved as Lost Passed Commentary)'
          }
        />
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.modalCancel}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnDanger}
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                category,
                note: note.trim() ? note.trim() : undefined,
              })
            }
          >
            {submitting ? 'Working…' : 'Confirm Pass'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wave B #10 — operator-driven merge target picker. Mirrors
// AttachToDealModal's shape but stripped of alias-token logic; merge
// is a pure rebind (no future-binding side effects to teach the
// resolver). Type-ahead against `/api/deal-cards/search` returns both
// candidates and Affinity opps; operator clicks a row to confirm.
interface MergeIntoDealModalProps {
  sourceLabel: string;
  sourceDealKind: 'candidate' | 'affinity_opp';
  sourceDealKey: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (target: {
    kind: 'candidate' | 'affinity_opp';
    deal_key: string;
    label: string;
  }) => void;
}

export function MergeIntoDealModal({
  sourceLabel, sourceDealKind, sourceDealKey, submitting,
  onClose, onSubmit,
}: MergeIntoDealModalProps) {
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Not gated on a non-empty query: the picker opens with the most recent deals (owner, 2026-08-05).
  const search = useDealPickerSearch(debouncedQ);
  const [picked, setPicked] = useState<{
    kind: 'candidate' | 'affinity_opp';
    deal_key: string;
    label: string;
  } | null>(null);

  // Filter the source from the results — can't merge a deal into itself.
  const results = useMemo(() => {
    if (!search.data) return [];
    const merged = [...search.data.candidates, ...search.data.affinity_opps];
    return merged.filter(
      (r) => !(r.kind === sourceDealKind && r.deal_key === sourceDealKey),
    );
  }, [search.data, sourceDealKind, sourceDealKey]);

  return (
    <div
      className={styles.modalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* role="dialog" — see LogPassModal. This one only escaped the same wrong-record bug
          by accident: its picker input has autoFocus, so the guard's focus arm happened to
          catch it. Accident is not a guarantee. */}
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Merge">
        <h3 className={styles.modalTitle}>Merge</h3>
        <div className={styles.modalSubtitle}>{sourceLabel}</div>
        <p style={{ fontSize: 13, color: '#1A1A1A', margin: '8px 0' }}>
          Pick the target deal. This card's events will be rebound to the
          target; this card will be dismissed with reason
          {' '}<code>merged_into_parent_opp</code>.
        </p>
        <input
          type="text"
          autoFocus
          className={styles.noteInput}
          placeholder="Search by company name, codename, or stage…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <div
          style={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid #E0E0DC',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {search.isLoading && (
            <div style={{ padding: 12, fontSize: 12, color: '#999' }}>
              Searching…
            </div>
          )}
          {!search.isLoading && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: '#999' }}>
              {debouncedQ.trim().length > 0 ? 'No matches.' : 'No deals to pick from.'}
            </div>
          )}
          {!search.isLoading && results.length > 0 && debouncedQ.trim().length === 0 && (
            <div style={{ padding: '6px 12px', fontSize: 11, color: '#999',
                          borderBottom: '1px solid #F0F0EC' }}>
              Most recent deals — live first. Type to search all deals, including lost and passed.
            </div>
          )}
          {results.map((r) => {
            const isPicked =
              picked !== null &&
              picked.kind === r.kind &&
              picked.deal_key === r.deal_key;
            return (
              <button
                key={`${r.kind}:${r.deal_key}`}
                type="button"
                onClick={() => setPicked({
                  kind: r.kind, deal_key: r.deal_key, label: r.label,
                })}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  borderBottom: '1px solid #F0F0EC',
                  background: isPicked ? '#E3F2FD' : 'transparent',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {r.label}
                  {' '}
                  <span style={{ fontWeight: 400, fontSize: 11, color: '#999' }}>
                    ({r.kind === 'candidate' ? 'candidate' : 'opp ' + r.deal_key})
                  </span>
                </div>
                {(r.sublabel || r.stage) && (
                  <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                    {[r.stage, r.sublabel].filter(Boolean).join(' · ')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.modalCancel}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={picked === null || submitting}
            onClick={() => {
              if (picked) onSubmit(picked);
            }}
          >
            {submitting ? 'Merging…' : `Merge into ${picked?.label ?? '…'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
